import { useCallback } from 'react';
import type { FieldValues, Path, UseFormSetError } from 'react-hook-form';
import { ApiError } from '../lib/api-client';
import type { MessageKey } from './en';
import { translateServerMessage } from './server-messages';
import { useT } from './useT';

const CODE_KEYS: Partial<Record<ApiError['code'], MessageKey>> = {
  NETWORK_ERROR: 'error.network',
  UNAUTHENTICATED: 'error.unauthenticated',
  FORBIDDEN: 'error.forbidden',
  NOT_FOUND: 'error.notFound',
  RATE_LIMITED: 'error.rateLimited',
  INTERNAL_ERROR: 'error.internal',
};

export interface ErrorTextHelpers {
  /** Localised text for any thrown value, for toasts and error states. */
  errorText: (error: unknown, fallback?: MessageKey) => string;
  /**
   * Maps the API's field-level details onto form fields, translating each
   * message. Returns false when nothing could be attached to a field.
   */
  applyApiError: <T extends FieldValues>(
    error: unknown,
    setError: UseFormSetError<T>,
    fields: ReadonlyArray<Path<T>>,
  ) => boolean;
}

export const useErrorText = (): ErrorTextHelpers => {
  const { t } = useT();

  const errorText = useCallback(
    (error: unknown, fallback?: MessageKey): string => {
      if (error instanceof ApiError) {
        const translated = translateServerMessage(error.message, t);
        if (translated) return translated;

        const byCode = CODE_KEYS[error.code];
        if (byCode) return t(byCode);

        // An unrecognised server message is still better than a generic one.
        return error.message;
      }

      if (fallback) return t(fallback);
      if (error instanceof Error) return error.message;
      return t('common.somethingWentWrong');
    },
    [t],
  );

  const applyApiError = useCallback(
    <T extends FieldValues>(
      error: unknown,
      setError: UseFormSetError<T>,
      fields: ReadonlyArray<Path<T>>,
    ): boolean => {
      if (!(error instanceof ApiError)) return false;

      let mapped = false;
      for (const detail of error.details) {
        const path = detail.path as Path<T>;
        if (!fields.includes(path)) continue;
        setError(path, {
          type: 'server',
          message: translateServerMessage(detail.message, t) ?? detail.message,
        });
        mapped = true;
      }

      if (!mapped && error.isValidation) {
        setError('root' as Path<T>, { type: 'server', message: errorText(error) });
        mapped = true;
      }

      return mapped;
    },
    [t, errorText],
  );

  return { errorText, applyApiError };
};
